import { useState } from "react";
import {
  Card,
  Form,
  Input,
  Button,
  Alert,
  Typography,
  Spin,
  Descriptions,
  Tag,
  message,
  Space,
} from "antd";
import { CarOutlined, SearchOutlined } from "@ant-design/icons";
import axios from "axios";

const { Title } = Typography;

// Configure message positioning to avoid hiding behind page elements
message.config({
  top: 80, // moves it down a bit from the top
  maxCount: 1, // how many messages can show at once
  duration: 3, // how many seconds the message stays
});

const VehicleVerification = () => {
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [vehicleData, setVehicleData] = useState(null);
  const [error, setError] = useState(null);

  const handleVerification = async (values) => {
    setLoading(true);
    setError(null);
    setVehicleData(null);

    try {
      const response = await axios.post(
        "https://www.piczmicroservices.com/live/backbone/public/v2/ratsa/vehicle-data",
        {
          registrationNo: values.registrationNo,
        },
        {
          headers: {
            Authorization: `Bearer 5KyNAAL86vpn4w3z7U4xv6f7JXvmSMQOzCW52cPrSraStUHKqeOI8MkH70AaIPQF23gngwL4adTX99S6`,
            "Content-Type": "application/json",
          },
        }
      );

      if (response.data.success) {
        setVehicleData(response.data.payload);
        message.success("Vehicle data retrieved successfully!");
      } else {
        setError(response.data.message || "Failed to retrieve vehicle data");
        message.error("Failed to retrieve vehicle data");
      }
    } catch (err) {
      console.error("Vehicle verification error:", err);
      setError(
        err.response?.data?.message ||
          "An error occurred while verifying the vehicle"
      );
      message.error("Failed to verify vehicle");
    } finally {
      setLoading(false);
    }
  };

  const getRegistrationStatusColor = (status) => {
    if (status?.toLowerCase().includes("registered")) {
      return "green";
    }
    if (status?.toLowerCase().includes("expired")) {
      return "red";
    }
    return "orange";
  };

  const formatDate = (dateString) => {
    if (!dateString) return "N/A";
    return new Date(dateString).toLocaleDateString();
  };

  return (
    <div style={{ padding: "24px", maxWidth: "800px", margin: "0 auto" }}>
      <Card>
        <div style={{ textAlign: "center", marginBottom: "24px" }}>
          <CarOutlined
            style={{ fontSize: "48px", color: "#1890ff", marginBottom: "16px" }}
          />
          <Title level={2}>Vehicle Verification</Title>
          <Typography.Text type="secondary">
            Enter a vehicle registration number to verify its details
          </Typography.Text>
        </div>

        <Form
          form={form}
          layout="vertical"
          onFinish={handleVerification}
          style={{ marginBottom: "24px" }}
        >
          <Form.Item
            label="Vehicle Registration Number"
            name="registrationNo"
            rules={[
              {
                required: true,
                message: "Please enter vehicle registration number",
              },
            ]}
          >
            <Input
              placeholder="e.g., BCE470"
              size="large"
              style={{ textTransform: "uppercase" }}
              onChange={(e) => {
                e.target.value = e.target.value.toUpperCase();
              }}
            />
          </Form.Item>

          <Form.Item>
            <Button
              type="primary"
              htmlType="submit"
              size="large"
              loading={loading}
              icon={<SearchOutlined />}
              block
            >
              {loading ? "Verifying..." : "Verify Vehicle"}
            </Button>
          </Form.Item>
        </Form>

        {loading && (
          <div style={{ textAlign: "center", padding: "24px" }}>
            <Spin size="large" />
            <div style={{ marginTop: "16px" }}>
              <Typography.Text>Verifying vehicle details...</Typography.Text>
            </div>
          </div>
        )}

        {error && (
          <Alert
            message="Verification Failed"
            description={error}
            type="error"
            showIcon
            style={{ marginBottom: "24px" }}
          />
        )}

        {vehicleData && (
          <Card
            title={
              <Space>
                <CarOutlined />
                Vehicle Details
              </Space>
            }
            style={{ marginTop: "24px" }}
          >
            <Descriptions bordered column={1} size="small">
              <Descriptions.Item label="Registration Number">
                <strong>{vehicleData.registrationNo}</strong>
              </Descriptions.Item>
              <Descriptions.Item label="Make">
                {vehicleData.make}
              </Descriptions.Item>
              <Descriptions.Item label="Model">
                {vehicleData.model}
              </Descriptions.Item>
              <Descriptions.Item label="Year of Manufacture">
                {vehicleData.yearMake}
              </Descriptions.Item>
              <Descriptions.Item label="Chassis Number">
                {vehicleData.chassisNo}
              </Descriptions.Item>
              <Descriptions.Item label="Engine Number">
                {vehicleData.engineNo}
              </Descriptions.Item>
              <Descriptions.Item label="Body Type">
                {vehicleData.bodyType}
              </Descriptions.Item>
              <Descriptions.Item label="Main Color">
                {vehicleData.mainColor}
              </Descriptions.Item>
              <Descriptions.Item label="Number of Seats">
                {vehicleData.numberOfSeats}
              </Descriptions.Item>
              <Descriptions.Item label="GVM (Gross Vehicle Mass)">
                {vehicleData.gvm} kg
              </Descriptions.Item>
              <Descriptions.Item label="First Registration Date">
                {formatDate(vehicleData.firstRegDate)}
              </Descriptions.Item>
              <Descriptions.Item label="License Expiry Date">
                {formatDate(vehicleData.currentLicenseExpiryDate)}
              </Descriptions.Item>
              <Descriptions.Item label="Road Worthiness Expiry">
                {formatDate(vehicleData.roadWorthinessExpiryDate)}
              </Descriptions.Item>
              <Descriptions.Item label="Registration Status">
                <Tag color={getRegistrationStatusColor(vehicleData.registrationStatus)}>
                  {vehicleData.registrationStatus}
                </Tag>
              </Descriptions.Item>
            </Descriptions>
          </Card>
        )}
      </Card>
    </div>
  );
};

export default VehicleVerification;